import { createContext, useContext, useState, useCallback, type ReactNode } from "react";

import type { RecommendationOutcome } from "./advisorLogic";

interface ProductAdvisorContextValue {
  isOpen: boolean;
  open: () => void;
  close: () => void;
  startAutoPrompt: boolean;
  dismissAutoPrompt: () => void;
  sessionId: string | null;
  startSession: () => void;
  endSession: () => void;
  markStep: (step: number) => void;
  markCompleted: () => void;
  markFormSubmitted: () => void;
  setRecommendedProducts: (products: string[]) => void;
  setAnswers: (answers: Record<string, string>) => void;
  lastOutcome: RecommendationOutcome | null;
  openToResults: boolean;
  jumpToResults: (outcome: RecommendationOutcome) => void;
}

const ProductAdvisorContext = createContext<ProductAdvisorContextValue | undefined>(undefined);

function generateId() {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

export function ProductAdvisorProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [startAutoPrompt, setStartAutoPrompt] = useState(true);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [lastOutcome, setLastOutcome] = useState<RecommendationOutcome | null>(null);
  const [openToResults, setOpenToResults] = useState(false);

  const startSession = useCallback(() => {
    setSessionId(generateId());
  }, []);

  const endSession = useCallback(() => {
    setSessionId(null);
  }, []);

  const markStep = useCallback((step: number) => {
    // step is tracked in panel, context just holds current step reference
    // sessionId stays active
    void step;
  }, []);

  const markCompleted = useCallback(() => {
    // session will be marked complete in panel logic
  }, []);

  const markFormSubmitted = useCallback(() => {
    // session will be marked form submitted in panel logic
  }, []);

  const setRecommendedProducts = useCallback((products: string[]) => {
    void products;
  }, []);

  const setAnswers = useCallback((answers: Record<string, string>) => {
    void answers;
  }, []);

  const open = useCallback(() => {
    setIsOpen(true);
    setStartAutoPrompt(false);
    setOpenToResults(false);
  }, []);

  const close = useCallback(() => {
    setIsOpen(false);
  }, []);

  const dismissAutoPrompt = useCallback(() => {
    setStartAutoPrompt(false);
  }, []);

  const jumpToResults = useCallback((outcome: RecommendationOutcome) => {
    setLastOutcome(outcome);
    setOpenToResults(true);
    setIsOpen(true);
    setStartAutoPrompt(false);
  }, []);

  return (
    <ProductAdvisorContext.Provider
      value={{
        isOpen,
        open,
        close,
        startAutoPrompt,
        dismissAutoPrompt,
        sessionId,
        startSession,
        endSession,
        markStep,
        markCompleted,
        markFormSubmitted,
        setRecommendedProducts,
        setAnswers,
        lastOutcome,
        openToResults,
        jumpToResults,
      }}
    >
      {children}
    </ProductAdvisorContext.Provider>
  );
}

export function useProductAdvisor() {
  const ctx = useContext(ProductAdvisorContext);
  if (!ctx) throw new Error("useProductAdvisor must be used within ProductAdvisorProvider");
  return ctx;
}
